import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';

export default function EuAiActGitHubCompliancePage() {
  const daysRemaining = Math.max(
    0,
    Math.ceil((new Date('2026-08-02').getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  );
  const deadlinePassed = daysRemaining === 0;

  const faqs = [
    {
      question: 'What GitHub practices satisfy EU AI Act Article 9 QMS requirements?',
      answer:
        'Article 9 requires a quality management system covering the full AI development lifecycle. In GitHub, this means: mandatory pull request reviews before any change to AI system code merges to production, required CI checks that validate model quality metrics and security, branch protection that prevents unauthorized changes, and documented exception records for any emergency deployments. The combination of PR approvals, CI check results, and branch protection snapshots constitutes the technical evidence layer of the QMS.',
    },
    {
      question: 'How does GitHub Copilot use relate to EU AI Act Article 14?',
      answer:
        'Article 14 requires human oversight of high-risk AI systems. When your team uses GitHub Copilot to help develop a high-risk AI system, auditors may ask how you ensure humans reviewed AI-generated code before it was deployed. The evidence is the co-author trailer ("Co-authored-by: GitHub Copilot") in commits, linked to the PR where a named human reviewer approved the change. EvidentTrail captures both the AI signal and the human approval in a single evidence record.',
    },
    {
      question: 'Which GitHub CI checks satisfy Article 15 accuracy and robustness requirements?',
      answer:
        'Article 15 requires that high-risk AI systems achieve appropriate accuracy and robustness. CI checks that satisfy this include: automated test suites that validate model output quality, integration tests that verify the system meets its intended purpose, security scanning (SAST/DAST) that addresses cybersecurity requirements, and model evaluation pipelines that measure accuracy metrics against defined thresholds. When these checks are required status checks in GitHub, their results are captured per PR as evidence that validation was performed before each deployment.',
    },
    {
      question: 'Does the EU AI Act require all code changes to be reviewed, or just AI model changes?',
      answer:
        'The EU AI Act requirements apply to the entire high-risk AI system — not just the model artifacts. Changes to inference code, data preprocessing pipelines, API layers, monitoring systems, and deployment configuration are all within scope if they affect the system\'s behavior, performance, or safety characteristics. In practice, this means that standard software change control procedures applied to all production code changes satisfy the QMS requirement for most teams.',
    },
    {
      question: 'Is there an EU AI Act deadline for having GitHub compliance evidence in place?',
      answer:
        'Requirements for Annex III high-risk AI systems become enforceable on August 2, 2026. If a market surveillance authority audits your QMS after that date, they can request evidence of how your change management procedure operated. Evidence collection should begin well before the deadline so that the evidence period predates the assessment date — starting now means you will have several months of evidence before enforcement begins.',
    },
    {
      question: 'Does EvidentTrail certify EU AI Act compliance?',
      answer:
        'No. EvidentTrail generates evidence packs that support QMS audit reviews. EU AI Act conformity assessment for Annex III systems requires either self-assessment against the harmonised standards or third-party assessment by a notified body. EvidentTrail provides the GitHub-sourced technical evidence that auditors and notified bodies request when reviewing your change management QMS documentation.',
    },
  ];

  const longformSections = [
    {
      title: 'GitHub as the technical backbone of your Article 9 QMS',
      paragraphs: [
        'Article 9 of the EU AI Act requires providers of high-risk AI systems to establish a quality management system covering design and development, testing and validation, change management, post-market monitoring, and corrective action processes. For software teams, GitHub is where the change management component of this QMS lives — and where the evidence that it operated as documented must come from.',
        'The pull request workflow is the technical implementation of your change management procedure. Every code change to the AI system goes through a pull request, is reviewed by at least one authorized person, passes required automated checks, and is not deployed until all approvals are in place. This is the QMS in action. The gap for most teams is not the process — it is the evidence that the process ran consistently across the development period.',
        'Market surveillance authorities and notified bodies conducting QMS reviews will request documentation and evidence of your change management procedure. They will want to see: the procedure itself (documented), evidence that it operated as documented (GitHub change records), and records of any deviations (exception records). EvidentTrail provides the second and third categories automatically — the procedure documentation is your responsibility.',
      ],
      bullets: [
        'Article 9 QMS: change management procedure must be documented and evidenced throughout the development lifecycle',
        'GitHub PR workflow = the technical implementation of your change management procedure',
        'Evidence required: proof the procedure operated consistently — not just that it was documented',
        'Surveillance authorities can request change records for any period after August 2, 2026',
      ],
    },
    {
      title: 'Article 14 human oversight: evidencing GitHub Copilot review',
      paragraphs: [
        'Article 14 requires that high-risk AI systems are designed and developed with human oversight mechanisms — natural persons must be able to monitor the AI system\'s operation and intervene. For development teams using AI coding assistants like GitHub Copilot, this creates a specific compliance question: how do you prove that AI-generated code contributions were reviewed by a human before they were incorporated into the high-risk AI system?',
        'GitHub Copilot records its contributions in commit metadata via co-author trailers: "Co-authored-by: GitHub Copilot <copilot@github.com>". This trailer is the machine-readable signal that AI contributed to the change. When that commit is part of a pull request that was reviewed and approved by a named human reviewer, the evidence chain is complete: AI contribution detected, human review assigned, human approval recorded.',
        'EvidentTrail captures both signals — the Copilot co-author trailer and the human reviewer approval — in a single evidence record linked to the PR. The evidence pack shows, for each AI-assisted change: what AI tool was involved, who the human reviewer was, when they approved, and what CI checks passed before the merge. This directly answers the Article 14 audit question.',
      ],
      bullets: [
        'Co-author trailer: "Co-authored-by: GitHub Copilot <copilot@github.com>" — machine-readable AI signal',
        'Human review: reviewer identity and approval timestamp linked to the AI signal in one record',
        'CI checks: required status checks passing before merge — additional evidence of quality gates',
        'Exception linkage: if an AI-assisted PR bypassed review, the exception record captures the bypass',
      ],
    },
    {
      title: 'Article 15 accuracy and robustness: CI checks as validation evidence',
      paragraphs: [
        'Article 15 requires that high-risk AI systems achieve an appropriate level of accuracy, robustness, and cybersecurity. For software teams, the CI pipeline is the primary mechanism for validating these properties before each change is deployed. Required status checks in GitHub — automated test suites, model evaluation pipelines, security scans, integration tests — are the evidence that validation was performed.',
        'When a CI check is a required status check in GitHub branch protection, it cannot be bypassed. The check must pass before the merge is permitted. EvidentTrail captures the check name, result (pass/fail), and timestamp for every required check on every merged PR. This creates a per-change validation record that auditors can use to verify that quality gates operated throughout the development period.',
        'For AI systems specifically, if model accuracy evaluation is part of the CI pipeline — a common practice for teams tracking model regression — the pass/fail record of that evaluation linked to each code change is direct evidence for Article 15 requirements. The CI check result is not a substitute for full model documentation, but it demonstrates that validation was integrated into the development process.',
      ],
      bullets: [
        'Required status checks: cannot be bypassed — pass/fail captured per PR with timestamps',
        'Model evaluation CI: accuracy and regression checks linked to each code change',
        'Security scanning: SAST, dependency scanning, secret detection satisfy cybersecurity requirements',
        'Coverage: every merged PR has CI evidence — no gaps in the validation record',
      ],
    },
    {
      title: 'GitHub configuration checklist for EU AI Act QMS',
      paragraphs: [
        'The following GitHub configuration satisfies the technical control requirements for an Article 9 QMS for software teams. These settings must be active continuously throughout the development period — not just configured in advance of an assessment. The configuration evidence (what settings were in place) and the change evidence (that the process operated) together constitute the QMS technical documentation.',
        'Branch protection on all production branches should require pull request reviews with at least one approver who is not the PR author (segregation of duty), require all defined status checks to pass before merge (testing and validation gate), include administrators in the protection rule to prevent bypass by any account, restrict direct push access to authorized deployment automation only, and disallow force pushes.',
        'For AI development teams specifically, required status checks should include model evaluation pipelines alongside standard test suites. If your team uses GitHub Copilot or other AI coding assistants, the co-author trailer policy should be documented — teams that want complete AI signal coverage can enforce trailer addition via pre-commit hooks or branch rules.',
      ],
      bullets: [
        'Require PR reviews: minimum 1 approver, not the PR author — segregation of duty for QMS',
        'Require status checks: test suites, model evaluation, security scans — all required before merge',
        'Include administrators: no account can bypass protection rules — applies to all team members',
        'Disallow force pushes: change history is immutable — consistent with QMS record-keeping',
        'AI signal policy: document Copilot co-author trailer enforcement for Article 14 evidence',
        'Audit log retention: ensure GitHub audit log export is configured for long-term evidence retention',
      ],
    },
    {
      title: 'What EU AI Act auditors ask about GitHub and QMS evidence',
      paragraphs: [
        'National market surveillance authorities and notified bodies conducting QMS reviews for Annex III systems typically request: technical documentation of the development lifecycle, evidence that change management procedures operated as documented, records of validation activities for each system version, and records of any deviations from the procedure.',
        'In the software development context, this translates to: pull request records showing authorization and review for each change to the AI system code, CI check results showing validation passed before deployment, branch protection configuration showing that access controls and review requirements were enforced, and exception records for any emergency deployments.',
        'The timing question is important. If an authority requests a QMS review after August 2, 2026, they may ask for evidence covering the period leading up to that date. Teams that start continuous evidence collection now will have a full evidence record available. Teams that start collecting evidence only after an audit request may not be able to reconstruct the historical record.',
      ],
      bullets: [
        'QMS documentation: the procedure plus evidence it operated — both are required',
        'Change records: every PR merged to production AI system code with approval and CI evidence',
        'Version validation: CI check results tied to each deployment — model evaluation and security scans',
        'Exception documentation: emergency deployments with justification and remediation records',
        'Start now: enforcement begins August 2, 2026 — evidence period should predate the assessment',
      ],
    },
  ];

  return (
    <MarketingLayout
      title="EU AI Act GitHub Compliance: QMS & Human Oversight Evidence | EvidentTrail"
      description="GitHub compliance guide for EU AI Act Article 9 QMS and Article 14 human oversight. Branch protection, CI validation evidence, and Copilot oversight records. August 2, 2026 enforcement deadline."
      h1="EU AI Act GitHub Compliance Guide"
      heroText="GitHub is where your Article 9 quality management system evidence lives. This guide covers the configuration, CI validation records, and Copilot oversight evidence that EU AI Act auditors request — with enforcement starting August 2, 2026."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'EU AI Act GitHub Compliance', url: 'https://evidenttrail.com/eu-ai-act-github-compliance' },
        ]}
      />
      <FaqJsonLd items={faqs} />

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">

            {/* Deadline banner */}
            <div className="mb-10 p-5 bg-amber-900/20 border border-amber-500/30 rounded-xl flex items-center gap-4">
              <div className="text-center min-w-[80px]">
                {deadlinePassed ? (
                  <div className="text-lg font-bold text-amber-400 leading-tight">In<br/>effect</div>
                ) : (
                  <>
                    <div className="text-3xl font-bold text-amber-400">{daysRemaining}</div>
                    <div className="text-xs text-amber-500 uppercase tracking-wide">days</div>
                  </>
                )}
              </div>
              <div>
                <p className="text-amber-300 font-semibold">
                  {deadlinePassed
                    ? 'August 2, 2026 — Annex III enforcement now active'
                    : 'August 2, 2026 — Annex III enforcement deadline'}
                </p>
                <p className="text-slate-400 text-sm mt-0.5">
                  {deadlinePassed
                    ? 'High-risk AI system QMS requirements are now enforceable. Compliance evidence must be in place.'
                    : 'High-risk AI system QMS requirements become enforceable. Evidence collection should begin before the assessment period starts.'}
                </p>
              </div>
            </div>

            {/* Free Tool CTA */}
            <div className="mb-12 p-6 bg-primary-900/20 border border-primary-400/30 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h2 className="text-white font-bold mb-2">Is your AI system high-risk under Annex III?</h2>
                  <p className="text-slate-300 text-sm mb-4">
                    The free AI Risk Classifier assesses your system against EU AI Act categories in 2 minutes and gives you a compliance checklist.
                  </p>
                  <Link to="/tools/ai-risk-classifier" className="btn btn-primary px-6 py-2.5 inline-block">
                    Try Free Risk Classifier →
                  </Link>
                </div>
                <div className="text-slate-500 text-xs text-right hidden sm:block">
                  <div>No signup required</div>
                  <div>2-minute assessment</div>
                  <div>Actionable checklist</div>
                </div>
              </div>
            </div>

            {/* Article mapping */}
            <h2 className={`${tokens.heading.h2} mb-6`}>EU AI Act Articles Covered by GitHub Evidence</h2>
            <div className="space-y-4 mb-12">
              {[
                {
                  article: 'Article 9',
                  name: 'Quality Management System',
                  requirement: 'QMS covering the full AI development lifecycle including change management, testing, and corrective action.',
                  evidence: 'PR approval records, CI check results, and branch protection snapshots constitute the change management evidence layer of the QMS.',
                },
                {
                  article: 'Article 11',
                  name: 'Technical Documentation',
                  requirement: "Technical documentation describing the system's design, development, testing, and intended purpose — producible on request.",
                  evidence: 'Evidence packs with SHA-256 checksums, covering the development period, satisfy the documentation retention requirement.',
                },
                {
                  article: 'Article 14',
                  name: 'Human Oversight',
                  requirement: 'Natural persons must be able to oversee and intervene. AI coding assistant contributions must demonstrate human review.',
                  evidence: 'Copilot co-author trailers linked to human reviewer approvals — the evidence chain from AI signal to human approval to production merge.',
                },
                {
                  article: 'Article 15',
                  name: 'Accuracy & Robustness',
                  requirement: 'Appropriate accuracy, robustness, and cybersecurity must be validated for each change to the system.',
                  evidence: 'Required CI status check results per PR — model evaluation, test suites, security scans — captured with timestamps and pass/fail.',
                },
                {
                  article: 'Article 17',
                  name: 'Post-Market Monitoring',
                  requirement: 'Systems must be monitored after deployment. Corrective actions must be documented when issues are identified.',
                  evidence: 'Branch protection drift events and exception records provide the corrective action documentation when controls are weakened or bypassed.',
                },
              ].map(({ article, name, requirement, evidence }) => (
                <div key={article} className={tokens.card}>
                  <div className="flex items-center justify-between mb-3 border-b border-slate-700 pb-2">
                    <h3 className="text-white font-bold">{name}</h3>
                    <span className="bg-amber-900/50 text-amber-300 px-2 py-0.5 rounded text-xs font-mono">{article}</span>
                  </div>
                  <p className="text-slate-500 text-xs mb-2 italic">{requirement}</p>
                  <div className="bg-slate-900/50 p-3 rounded-lg">
                    <span className="text-primary-400 text-xs font-medium">EvidentTrail evidence: </span>
                    <span className="text-slate-300 text-sm">{evidence}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Risk Tiers */}
            <h2 className={`${tokens.heading.h2} mb-6`}>EU AI Act Risk Tiers</h2>
            <div className="overflow-x-auto mb-12">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Risk Tier</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Examples</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Requirements</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {[
                    ['Prohibited (Article 5)', 'Social scoring, real-time biometric surveillance', 'Banned — cannot be developed or deployed'],
                    ['High-Risk (Annex III)', 'Employment AI, credit scoring, education AI', 'Full QMS, technical docs, human oversight, conformity assessment'],
                    ['Limited Risk', 'Chatbots, deepfake generation', 'Transparency obligations — disclose AI interaction to users'],
                    ['Minimal Risk', 'Spam filters, AI in games', 'No specific obligations — voluntary codes of conduct'],
                  ].map(([tier, examples, reqs]) => (
                    <tr key={tier} className="hover:bg-slate-800/30">
                      <td className="py-3 px-4 text-slate-300 font-medium">{tier}</td>
                      <td className="py-3 px-4 text-slate-400">{examples}</td>
                      <td className="py-3 px-4 text-slate-400">{reqs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Copilot evidence chain */}
            <h2 className={`${tokens.heading.h2} mb-6`}>Article 14 Evidence Chain: AI Signal to Human Approval</h2>
            <div className="relative mb-12">
              {[
                {
                  step: '1',
                  title: 'AI Contribution Detected',
                  detail: 'Copilot co-author trailer captured from commit metadata. Timestamp recorded. No code content stored.',
                  badge: 'Article 14',
                },
                {
                  step: '2',
                  title: 'PR Opened for Review',
                  detail: 'Branch protection requires reviewer assignment before merge. Reviewer identity recorded.',
                  badge: 'Article 9',
                },
                {
                  step: '3',
                  title: 'Validation Checks Pass',
                  detail: 'Required CI checks — tests, model evaluation, security scans — pass before merge is permitted.',
                  badge: 'Article 15',
                },
                {
                  step: '4',
                  title: 'Human Approval Recorded',
                  detail: 'Named reviewer approves. Timestamp linked to AI signal. Segregation of duty enforced — author cannot self-approve.',
                  badge: 'Article 14',
                },
                {
                  step: '5',
                  title: 'Evidence Pack Generated',
                  detail: 'AI signal → Reviewer identity → CI results → Approval → Merge. SHA-256 checksum. Exportable for QMS documentation.',
                  badge: 'Article 9',
                },
              ].map(({ step, title, detail, badge }) => (
                <div key={step} className="flex items-start gap-4 mb-4">
                  <div className="w-9 h-9 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold text-sm">
                    {step}
                  </div>
                  <div className={`${tokens.card} flex-1`}>
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-white font-semibold text-sm">{title}</h3>
                      <span className="bg-amber-900/50 text-amber-300 px-2 py-0.5 rounded text-xs font-mono">{badge}</span>
                    </div>
                    <p className="text-sm text-slate-400">{detail}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <a href={appLinks.register} className="btn btn-primary px-8 py-3">
                Start Building EU AI Act Evidence
              </a>
            </div>
          </div>
        </div>
      </section>

      <SeoLongformSections sections={longformSections} faqs={faqs} />

      <section className={tokens.section}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related EU AI Act Resources</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <Link to="/soc2-change-management-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">SOC 2 Change Evidence</h4>
              <p className="text-sm text-slate-400">The same GitHub evidence satisfies SOC 2 CC8.1 change management requirements.</p>
            </Link>
            <Link to="/github-copilot-compliance" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">GitHub Copilot Compliance</h4>
              <p className="text-sm text-slate-400">Detect Copilot signals and link them to human reviewer approvals.</p>
            </Link>
            <Link to="/tools/ai-risk-classifier" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">AI Risk Classifier</h4>
              <p className="text-sm text-slate-400">Free tool — assess your EU AI Act risk level in 2 minutes.</p>
            </Link>
            <Link to="/ai-assisted-development-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">AI-Assisted Dev Evidence</h4>
              <p className="text-sm text-slate-400">Broader overview of AI coding assistant audit evidence requirements.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
